import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

/**
 * Reads the cached credentials that the Cognito SDK persists to local storage on
 * sign in.
 *
 * `username` is the value used as the local-storage key (our callbacks cache the
 * session under the user's email). `hashUsername` is the actual Cognito username
 * (`cognito:username` claim) that Cognito uses to verify the `SECRET_HASH` — for
 * federated logins this is a provider-prefixed value (e.g. `google_1234...`),
 * not the email.
 */
const getCachedCredentials = (): {
  username: string | null;
  hashUsername: string | null;
  refreshToken: string | null;
} => {
  const clientId = process.env.NEXT_PUBLIC_COGNITO_USER_POOL_WEB_CLIENT_ID;
  const prefix = `CognitoIdentityServiceProvider.${clientId}`;
  const username = window.localStorage.getItem(`${prefix}.LastAuthUser`);

  if (!username) {
    return { username: null, hashUsername: null, refreshToken: null };
  }

  const refreshToken = window.localStorage.getItem(
    `${prefix}.${username}.refreshToken`,
  );

  const rawIdToken = window.localStorage.getItem(
    `${prefix}.${username}.idToken`,
  );
  const hashUsername = rawIdToken
    ? (new CognitoIdToken({ IdToken: rawIdToken }).payload[
        "cognito:username"
      ] as string) || username
    : username;

  return { username, hashUsername, refreshToken };
};

/**
 * Exchanges the refresh token for a fresh session via the Cloudflare worker
 * endpoint, then re-injects the new session into the Cognito SDK's local
 * storage so subsequent SDK calls succeed.
 *
 * Our Cognito app client has a client secret, so the browser SDK cannot perform
 * its own `REFRESH_TOKEN_AUTH` (it can't compute a `SECRET_HASH`). The worker
 * holds the secret and performs the refresh server side.
 */
const refreshSession = async (
  username: string,
  hashUsername: string,
  refreshToken: string,
): Promise<void> => {
  const base = process.env.NEXT_PUBLIC_CLOUDFLARE_WORKER_API_HOST || "";

  const response = await fetch(`${base}/api/v1/login/refresh`, {
    method: "POST",
    body: JSON.stringify({ refreshToken, username: hashUsername }),
  });

  if (response.status !== 200) {
    throw new Error("Failed to refresh session");
  }

  const { AccessToken, IdToken } = await response.json();

  const accessToken = new CognitoAccessToken({ AccessToken });
  const idToken = new CognitoIdToken({ IdToken });
  // REFRESH_TOKEN_AUTH does not return a new refresh token (rotation is off),
  // so we reuse the existing one.
  const refreshTokenObject = new CognitoRefreshToken({
    RefreshToken: refreshToken,
  });

  const session = new CognitoUserSession({
    IdToken: idToken,
    AccessToken: accessToken,
    RefreshToken: refreshTokenObject,
  });

  const userPool = new CognitoUserPool({
    UserPoolId: process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID,
    ClientId: process.env.NEXT_PUBLIC_COGNITO_USER_POOL_WEB_CLIENT_ID,
  });

  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  });

  cognitoUser.setSignInUserSession(session);
};

/**
 * Calls the Chord `getToken` action and, if it fails because the access token
 * has expired, refreshes the session server side and retries.
 *
 * The refresh token must be captured before calling `getToken`: when the access
 * token has expired, the Cognito SDK attempts its own browser-side refresh,
 * which fails for our secret-enabled app client and clears the cached tokens
 * from local storage.
 */
export const getTokenWithRefresh = async (
  getToken: () => Promise<string>,
): Promise<string> => {
  const { username, hashUsername, refreshToken } =
    typeof window !== "undefined"
      ? getCachedCredentials()
      : { username: null, hashUsername: null, refreshToken: null };

  try {
    return await getToken();
  } catch (error) {
    if (!username || !hashUsername || !refreshToken) throw error;
    await refreshSession(username, hashUsername, refreshToken);
    return getToken();
  }
};
